import { useState, useCallback, useRef } from "react";

// ─── Framework Definitions ──────────────────────────────────────────────────

const FRAMEWORKS = {
  NIST_CSF: {
    id: "NIST_CSF",
    name: "NIST CSF 2.0",
    subtitle: "Cybersecurity Framework",
    badge: "CSF",
    color: "#0ea5e9",
    icon: "⬡",
    audience: "All organizations",
    tagline: "Enterprise cybersecurity posture across 6 functions",
    domains: [
      { id: "GV", name: "Govern",   color: "#6366f1", weight: 22,
        controls: ["Org Context","Risk Mgmt Strategy","Roles & Responsibilities","Policy","Oversight","Supply Chain Risk"] },
      { id: "ID", name: "Identify", color: "#f59e0b", weight: 18,
        controls: ["Asset Management","Risk Assessment","Improvement"] },
      { id: "PR", name: "Protect",  color: "#10b981", weight: 20,
        controls: ["Identity & Access","Awareness & Training","Data Security","Platform Security","Infra Resilience"] },
      { id: "DE", name: "Detect",   color: "#ef4444", weight: 12,
        controls: ["Continuous Monitoring","Adverse Event Analysis"] },
      { id: "RS", name: "Respond",  color: "#f97316", weight: 16,
        controls: ["Incident Mgmt","Incident Analysis","Reporting & Comms","Incident Mitigation"] },
      { id: "RC", name: "Recover",  color: "#8b5cf6", weight: 12,
        controls: ["Recovery Plan Execution"] },
    ],
    scoring: { type: "tier", min: 1, max: 4,
      labels: { 1: "Partial", 2: "Risk-Informed", 3: "Repeatable", 4: "Adaptive" } },
  },

  CMMC: {
    id: "CMMC",
    name: "CMMC 2.0",
    subtitle: "Level 2 — 110 Practices",
    badge: "L2",
    color: "#f59e0b",
    icon: "⬢",
    audience: "Defense Industrial Base (DIB)",
    tagline: "All 110 NIST 800-171 practices for CUI protection",
    domains: [
      { id: "AC",  name: "Access Control",            color: "#0ea5e9", weight: 22,
        controls: ["AC.1.001 Authorized Access","AC.1.002 Limit Transactions","AC.2.005 Sensitive Info Notification","AC.2.006 Privileged Accounts","AC.2.007 Least Privilege","AC.3.017 Separation of Duties","AC.3.018 Non-Privileged Accounts","AC.3.019 Unsuccessful Logon","AC.3.021 Remote Access Authorizations","AC.3.022 Cryptographic Remote Access"] },
      { id: "AU",  name: "Audit & Accountability",    color: "#6366f1", weight: 9,
        controls: ["AU.2.041 User Actions","AU.2.042 Audit Logging","AU.3.045 Audit Reviews","AU.3.046 Reduce/Protect Logs"] },
      { id: "AT",  name: "Awareness & Training",      color: "#84cc16", weight: 3,
        controls: ["AT.2.056 Cyber Awareness Training","AT.3.058 Insider Threat Awareness"] },
      { id: "CM",  name: "Config Management",         color: "#14b8a6", weight: 9,
        controls: ["CM.2.061 Baseline Configs","CM.2.062 Software Restrictions","CM.2.064 Security Config Settings","CM.3.068 Deny-by-exception"] },
      { id: "IA",  name: "Identification & Auth",     color: "#ec4899", weight: 11,
        controls: ["IA.1.076 Unique User IDs","IA.1.077 Password Authenticate","IA.3.083 MFA for CUI Access","IA.3.085 Employ Replay-Resistant Auth"] },
      { id: "IR",  name: "Incident Response",         color: "#ef4444", weight: 3,
        controls: ["IR.2.092 Incident Capability","IR.2.093 Track/Document Incidents","IR.2.094 Test IR Capability"] },
      { id: "MA",  name: "Maintenance",               color: "#f97316", weight: 6,
        controls: ["MA.2.111 Perform Maintenance","MA.2.112 Sanitize Equipment","MA.3.115 Multi-factor Auth Maintenance"] },
      { id: "MP",  name: "Media Protection",          color: "#a855f7", weight: 9,
        controls: ["MP.1.118 Protect CUI on Media","MP.2.119 Limit Access to CUI","MP.3.122 Mark Media","MP.3.123 Sanitize/Destroy Media"] },
      { id: "PS",  name: "Personnel Security",        color: "#0d9488", weight: 2,
        controls: ["PS.2.127 Screen Individuals","PS.2.128 Terminate/Transfer Actions"] },
      { id: "PE",  name: "Physical Protection",       color: "#b45309", weight: 6,
        controls: ["PE.1.131 Limit Physical Access","PE.1.132 Escort Visitors","PE.2.135 Control Visitor Access"] },
      { id: "RA",  name: "Risk Assessment",           color: "#7c3aed", weight: 3,
        controls: ["RA.2.141 Risk Assessment","RA.2.142 Vulnerability Scan","RA.3.144 Periodically Assess Risk"] },
      { id: "CA",  name: "Security Assessment",       color: "#0891b2", weight: 4,
        controls: ["CA.2.157 Assess Security Controls","CA.2.158 Develop Action Plan","CA.2.159 Monitor Security Controls"] },
      { id: "SC",  name: "System & Comms Protection", color: "#dc2626", weight: 16,
        controls: ["SC.1.175 Monitor Comms Boundaries","SC.3.177 FIPS Cryptography","SC.3.180 Architectural Designs","SC.3.187 Session Authenticity"] },
      { id: "SI",  name: "System & Info Integrity",   color: "#65a30d", weight: 7,
        controls: ["SI.1.210 Malicious Code Protection","SI.2.214 Security Alerts","SI.3.218 Security Capabilities","SI.3.220 Email Forgery Protection"] },
    ],
    scoring: { type: "met", options: ["MET","PARTIAL","NOT MET"],
      colors: { MET: "#10b981", PARTIAL: "#f59e0b", "NOT MET": "#ef4444" } },
    sprs: true,
  },

  CJIS: {
    id: "CJIS",
    name: "CJIS v6.0",
    subtitle: "Security Policy — Dec 2024",
    badge: "v6",
    color: "#8b5cf6",
    icon: "⬟",
    audience: "Law enforcement & vendors with CJI access",
    tagline: "20 policy areas, 180+ P1 controls auditable Oct 2024",
    domains: [
      { id: "PA5.1",  name: "Information Exchange Agreements", color: "#6366f1", weight: 8, priority: "P1",
        controls: ["CJIS User Agreements","Management Control Agreements","NCJA Agreements","Interagency Connection Agreements","Security Addendum Execution","Vendor/Contractor Agreements"] },
      { id: "PA5.2",  name: "Awareness & Training",           color: "#0ea5e9", weight: 7, priority: "P1",
        controls: ["Security Awareness (L1-L4)","Role-Based Training","Training Records","Training Frequency (every 2 yrs)","Insider Threat Awareness"] },
      { id: "PA5.3",  name: "Incident Response",              color: "#ef4444", weight: 9, priority: "P1",
        controls: ["IR Plan Documented","IR Team Designated","Incident Reporting to FBI","IR Plan Tested Annually","Forensic Readiness","Post-Incident Review"] },
      { id: "PA5.4",  name: "Auditing & Accountability",      color: "#f59e0b", weight: 10, priority: "P1",
        controls: ["Audit Log Generation","Audit Log Review","Log Retention (min 90 days)","Audit Reduction Tools","Time-Source Synchronization","Protection of Audit Info"] },
      { id: "PA5.5",  name: "Access Control",                 color: "#10b981", weight: 12, priority: "P1",
        controls: ["Account Management","Least Privilege","Separation of Duties","Remote Access Controls","Wireless Access Controls","Mobile Device Access","Account Lockout (5 failures)","Session Lock (30 min idle)","Disable Accounts within 24hrs of Termination"] },
      { id: "PA5.6",  name: "Identification & Authentication",color: "#ec4899", weight: 11, priority: "P1",
        controls: ["Unique User IDs","MFA for Remote Access","MFA for All CJI Access (Oct 2024)","Password Complexity (≥15 chars)","Authenticator Management","Device Authentication","PKI / Smart Card Support"] },
      { id: "PA5.7",  name: "Configuration Management",       color: "#14b8a6", weight: 9, priority: "P1/P2",
        controls: ["Baseline Configuration","Configuration Change Control","Security Impact Analysis","Least Functionality","Software Usage Restrictions","Application Allowlisting","Automated Config Baselines (P2)"] },
      { id: "PA5.8",  name: "Media Protection",               color: "#a855f7", weight: 7, priority: "P1",
        controls: ["Media Access Controls","Media Marking","Media Storage","Media Transport","Media Sanitization","Media Use Restrictions"] },
      { id: "PA5.9",  name: "Physical Protection",            color: "#f97316", weight: 8, priority: "P1",
        controls: ["Physical Access Authorizations","Card Reader / Badge Controls","Visitor Escort & Logs","Surveillance Cameras (server rooms)","CJI Location Restricted Access","Physical Perimeter Controls"] },
      { id: "PA5.10", name: "Systems & Comms Protection",     color: "#0891b2", weight: 10, priority: "P1",
        controls: ["Network Segmentation","FIPS 140-2 Encryption at Rest","FIPS 140-2 Encryption in Transit","TLS 1.2 Minimum","Boundary Protection","Session Authenticity","Mobile Device Encryption","Remote Wipe Capability"] },
      { id: "PA5.11", name: "Formal Audits",                  color: "#b45309", weight: 5, priority: "Triennial",
        controls: ["FBI / State CSA Triennial Audit","Corrective Action Plan","Self-Audit Program","Audit Scheduling with State CSA"] },
      { id: "PA5.12", name: "Personnel Security",             color: "#7c3aed", weight: 7, priority: "P1",
        controls: ["Background Checks (fingerprint)","Security Addendum Signed","Contractor Screening","Access Termination Process","User Registration Process"] },
      { id: "PA5.13", name: "Mobile Devices",                 color: "#dc2626", weight: 8, priority: "P1",
        controls: ["MDM Enrollment Required","Full Device Encryption","Remote Wipe Enabled","App Controls / Allowlisting","Device-Level Authentication","BYOD Controls","Screen Lock (10 min)"] },
      { id: "PA5.14", name: "System & Info Integrity",        color: "#65a30d", weight: 8, priority: "P1/P2",
        controls: ["Malware Protection","Security Alerts & Advisories","Vulnerability Scanning","Patch Management","Spam Protection","Input Validation (P2)"] },
      { id: "PA5.15", name: "Risk Management",                color: "#0d9488", weight: 7, priority: "P1",
        controls: ["Risk Assessments","Risk Response","Risk Monitoring","Vulnerability Disclosure","Supply Chain Risk Mgmt Plan (v6.0 new)"] },
      { id: "PA5.16", name: "Cloud Computing",                color: "#6366f1", weight: 8, priority: "P1",
        controls: ["Cloud Service Vetting (CJIS-compliant)","Data Residency (US only)","Cloud Access Controls","CSP CJIS Addendum","FedRAMP Moderate baseline preferred","Data Isolation"] },
      { id: "PA5.17", name: "Virtualization",                 color: "#f59e0b", weight: 5, priority: "P2",
        controls: ["Hypervisor Hardening","VM Isolation","Snapshot Controls","Virtual Network Segmentation"] },
      { id: "PA5.18", name: "Outsourcing",                    color: "#ef4444", weight: 6, priority: "P1",
        controls: ["Vendor CJIS Agreements","Vendor Security Assessment","SLA with Security Requirements","Vendor Breach Notification","Sub-contractor Controls"] },
      { id: "PA5.19", name: "Supply Chain Risk Mgmt",         color: "#8b5cf6", weight: 6, priority: "P1 (v6.0 new)",
        controls: ["SCRM Plan Established","Technology Acquisition Review","Third-Party Incident Notification","Component Authenticity Verification","Critical Supplier Assessment"] },
      { id: "PA5.20", name: "Digital Evidence",               color: "#10b981", weight: 5, priority: "P2",
        controls: ["Evidence Integrity Controls","Chain of Custody Documentation","Evidence Storage Controls","Legal Admissibility Standards"] },
    ],
    scoring: { type: "met", options: ["MET","PARTIAL","NOT MET"],
      colors: { MET: "#10b981", PARTIAL: "#f59e0b", "NOT MET": "#ef4444" } },
    priorityDeadlines: {
      "P1": "Auditable Oct 1, 2024 — SANCTIONABLE NOW",
      "P2": "Auditable Oct 1, 2027",
      "P3": "Auditable Oct 1, 2027",
      "P4": "Auditable Oct 1, 2027",
      "Triennial": "Every 3 years",
      "P1/P2": "P1 portion auditable now",
      "P1 (v6.0 new)": "New in v6.0 — P1 sanctionable",
    },
  },
};

// ─── AI Report Generator ────────────────────────────────────────────────────
async function generateReport(framework, orgName, orgType, scores) {
  const fw = FRAMEWORKS[framework];
  const scoreLines = fw.domains.map(d => {
    const s = scores[d.id];
    if (fw.scoring.type === "tier") {
      return `${d.id} ${d.name}: Current Tier ${s?.current ?? 2}/4, Target ${s?.target ?? 3}/4`;
    } else {
      const met = d.controls.filter((_, i) => s?.controls?.[i] === "MET").length;
      const partial = d.controls.filter((_, i) => s?.controls?.[i] === "PARTIAL").length;
      const notMet = d.controls.filter((_, i) => s?.controls?.[i] === "NOT MET").length;
      return `${d.id} ${d.name}: ${met} MET / ${partial} PARTIAL / ${notMet} NOT MET (of ${d.controls.length})`;
    }
  }).join("\n");

  const sprsNote = fw.sprs ? `\nFor CMMC: calculate SPRS score (starts at 110, subtract weights for NOT MET practices). Estimate the SPRS score range based on the NOT MET count.` : "";
  const cjisNote = framework === "CJIS" ? `\nFor CJIS: call out which findings are P1 (immediately sanctionable as of Oct 2024) vs P2-P4 (auditable 2027). The organization faces real legal risk for P1 gaps today.` : "";

  const prompt = `You are a senior GRC consultant at CyberEye Solutions performing a ${fw.name} ${fw.subtitle} assessment.

Organization: ${orgName}
Industry: ${orgType}
Framework: ${fw.name} — ${fw.subtitle}
Assessment Date: ${new Date().toLocaleDateString()}

DOMAIN SCORES:
${scoreLines}
${sprsNote}${cjisNote}

Respond ONLY with valid JSON (no backticks, no markdown, no preamble):
{
  "overallRating": "Critical Risk|High Risk|Moderate Risk|Managed Risk|Compliant",
  "executiveSummary": "3-4 sentence summary specific to their scores, key risk drivers, and immediate priorities.",
  ${fw.sprs ? `"sprsScore": <estimated integer 0-110>,` : ""}
  ${framework === "CJIS" ? `"p1GapsCount": <integer count of P1 policy areas with NOT MET findings>,` : ""}
  "topFindings": [
    {
      "domainId": "<domain id>",
      "domainName": "<name>",
      "severity": "Critical|High|Medium|Low",
      "finding": "Specific finding (1-2 sentences).",
      "riskIfUnaddressed": "Concrete consequence (1 sentence).",
      "recommendation": "Actionable fix (1-2 sentences)."
    }
  ],
  "quickWins": [
    { "action": "Specific action title", "effort": "Low|Medium", "impact": "High|Medium", "owner": "Role", "days": <int estimated days> }
  ],
  "roadmap": [
    { "phase": "0-90 days", "initiatives": ["Short initiative title"] },
    { "phase": "3-12 months", "initiatives": ["Short initiative title"] },
    { "phase": "12-24 months", "initiatives": ["Short initiative title"] }
  ],
  "domainScores": [
    { "domain": "<name>", "score": <0-100 percentage of controls met or tier pct>, "color": "<domain hex color>" }
  ]
}

topFindings: exactly 4 findings, most critical first. quickWins: exactly 3 items. roadmap phases: 2-3 initiatives each. domainScores: one entry per domain with % compliance. Be specific to their actual scores.`;

  const res = await fetch("/api/assess", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "llama-3.3-70b-versatile",
      max_tokens: 1000,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  const data = await res.json();
  const text = data.choices?.[0]?.message?.content || "";
  return JSON.parse(text.replace(/```json\n?|```\n?/g, "").trim());
}

// ─── Horizontal Bar Chart ───────────────────────────────────────────────────
function DomainBar({ name, score, color, maxWidth = 200 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
      <div style={{ width: 130, fontSize: 10, color: "#94a3b8", textAlign: "right", flexShrink: 0, lineHeight: 1.2 }}>{name}</div>
      <div style={{ width: maxWidth, height: 8, background: "rgba(255,255,255,0.07)", borderRadius: 4, flexShrink: 0, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(score, 100)}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.6s ease", opacity: 0.85 }} />
      </div>
      <div style={{ fontSize: 11, color, fontWeight: 700, width: 38, fontFamily: "IBM Plex Mono, monospace" }}>{Math.round(score)}%</div>
    </div>
  );
}

// ─── Main App ────────────────────────────────────────────────────────────────
export default function MultiFrameworkAssessor() {
  const [step, setStep]             = useState("select");   // select | intake | scoring | analyzing | results
  const [framework, setFramework]   = useState(null);
  const [orgName, setOrgName]       = useState("");
  const [orgType, setOrgType]       = useState("Defense Industrial Base (DIB)");
  const [activeDomain, setActiveDomain] = useState(0);
  const [scores, setScores]         = useState({});
  const [progress, setProgress]     = useState(0);
  const [results, setResults]       = useState(null);
  const [error, setError]           = useState(null);
  const timerRef                    = useRef(null);

  const fw = framework ? FRAMEWORKS[framework] : null;

  // Initialize scores when framework chosen
  const selectFramework = (fwId) => {
    const f = FRAMEWORKS[fwId];
    const init = {};
    f.domains.forEach(d => {
      if (f.scoring.type === "tier") {
        init[d.id] = { current: 2, target: 3 };
      } else {
        init[d.id] = { controls: d.controls.map(() => "NOT MET") };
      }
    });
    setScores(init);
    setFramework(fwId);
    setActiveDomain(0);
    setStep("intake");
  };

  const setControlScore = (domainId, idx, val) => {
    setScores(prev => ({
      ...prev,
      [domainId]: { ...prev[domainId], controls: prev[domainId].controls.map((c, i) => i === idx ? val : c) }
    }));
  };

  const setTier = (domainId, field, val) => {
    setScores(prev => ({ ...prev, [domainId]: { ...prev[domainId], [field]: val } }));
  };

  const runAnalysis = useCallback(async () => {
    setStep("analyzing"); setProgress(0); setError(null);
    let p = 0;
    timerRef.current = setInterval(() => {
      p += Math.random() * 9; if (p > 91) p = 91;
      setProgress(Math.round(p));
    }, 180);
    try {
      const r = await generateReport(framework, orgName, orgType, scores);
      clearInterval(timerRef.current);
      setProgress(100);
      await new Promise(res => setTimeout(res, 350));
      setResults(r);
      setStep("results");
    } catch (e) {
      clearInterval(timerRef.current);
      setError("Analysis failed — check connection and retry.");
      setStep("scoring");
    }
  }, [framework, orgName, orgType, scores]);

  // computed completion %
  const completionPct = fw ? (() => {
    if (fw.scoring.type === "tier") return 100;
    const total = fw.domains.reduce((s, d) => s + d.controls.length, 0);
    const scored = fw.domains.reduce((s, d) => {
      const sc = scores[d.id];
      return s + (sc?.controls?.filter(c => c !== "NOT MET").length ?? 0);
    }, 0);
    return Math.round((scored / total) * 100);
  })() : 0;

  // ── FRAMEWORK SELECT ──────────────────────────────────────────────────────
  if (step === "select") return (
    <div style={css.root}>
      <div style={css.bg} />
      <div style={css.center}>
        <div style={css.selectHeader}>
          <Logo />
          <h1 style={css.selectTitle}>Multi-Framework Assessor</h1>
          <p style={css.selectSub}>Select a compliance framework to begin your CyberEye readiness assessment</p>
        </div>
        <div style={css.fwGrid}>
          {Object.values(FRAMEWORKS).map(f => (
            <button key={f.id} style={{ ...css.fwCard, borderTopColor: f.color }} onClick={() => selectFramework(f.id)}>
              <div style={{ ...css.fwBadge, background: f.color + "20", color: f.color, border: `1px solid ${f.color}40` }}>
                {f.badge}
              </div>
              <div style={{ ...css.fwIcon, color: f.color }}>{f.icon}</div>
              <div style={css.fwName}>{f.name}</div>
              <div style={css.fwSub}>{f.subtitle}</div>
              <div style={css.fwAudience}>{f.audience}</div>
              <div style={css.fwTagline}>{f.tagline}</div>
              <div style={{ ...css.fwStart, color: f.color }}>Begin Assessment →</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── INTAKE ────────────────────────────────────────────────────────────────
  if (step === "intake") return (
    <div style={css.root}>
      <div style={css.bg} />
      <div style={css.center}>
        <div style={css.intakeCard}>
          <button style={css.backBtn} onClick={() => setStep("select")}>← Back</button>
          <div style={{ ...css.fwBadge, background: fw.color + "20", color: fw.color, border: `1px solid ${fw.color}40`, marginBottom: 16, display: "inline-block" }}>
            {fw.badge}
          </div>
          <h2 style={css.intakeTitle}>{fw.name} — {fw.subtitle}</h2>
          <p style={css.intakeSub}>{fw.tagline}</p>
          {framework === "CJIS" && (
            <div style={css.cjisAlert}>
              <span style={{ color: "#ef4444", marginRight: 6 }}>⚠</span>
              <span><strong style={{ color: "#f87171" }}>P1 controls are auditable and sanctionable NOW</strong> (Oct 1, 2024). P2–P4 controls become fully auditable Oct 1, 2027. CJIS v6.0 adds Supply Chain Risk Mgmt and expanded Mobile Device requirements.</span>
            </div>
          )}
          {framework === "CMMC" && (
            <div style={css.cjisAlert}>
              <span style={{ color: "#f59e0b", marginRight: 6 }}>⚠</span>
              <span><strong style={{ color: "#fbbf24" }}>DFARS 252.204-7021</strong> requires annual SPRS score submission. C3PAO assessments now required for contracts with CUI. Your SPRS score starting at 110 will be computed from NOT MET practices.</span>
            </div>
          )}
          <div style={css.intakeForm}>
            <div style={css.formField}>
              <label style={css.label}>Organization Name *</label>
              <input style={css.input} placeholder="Acme Defense LLC" value={orgName} onChange={e => setOrgName(e.target.value)} />
            </div>
            <div style={css.formField}>
              <label style={css.label}>Industry / Org Type</label>
              <select style={css.input} value={orgType} onChange={e => setOrgType(e.target.value)}>
                {["Defense Industrial Base (DIB)","Law Enforcement Agency","County / Municipal Government","911 / Emergency Dispatch","State IT Agency","Federal Contractor","Healthcare / HIPAA","Technology / SaaS","Financial Services","Other Government Contractor"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <button style={{ ...css.primaryBtn, background: fw.color, opacity: orgName.trim() ? 1 : 0.4 }}
            disabled={!orgName.trim()} onClick={() => setStep("scoring")}>
            Start Scoring — {fw.domains.length} Domains →
          </button>
        </div>
      </div>
    </div>
  );

  // ── ANALYZING ─────────────────────────────────────────────────────────────
  if (step === "analyzing") return (
    <div style={css.root}>
      <div style={css.bg} />
      <div style={css.center}>
        <div style={{ textAlign: "center", maxWidth: 380 }}>
          <div style={{ fontSize: 48, marginBottom: 20, animation: "pulse 1.5s ease-in-out infinite" }}>
            {fw.icon}
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", margin: "0 0 8px" }}>Analyzing {fw.name}</h2>
          <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 28px" }}>CyberEye AI processing {fw.domains.length} domains for {orgName}…</p>
          <div style={css.progressTrack}><div style={{ ...css.progressFill, width: `${progress}%`, background: fw.color }} /></div>
          <div style={{ fontSize: 11, color: "#475569", marginTop: 6, textAlign: "right" }}>{progress}%</div>
          {["Aggregating domain scores","Running gap analysis","Calculating compliance score","Generating roadmap","Mapping to regulatory deadlines"].map((s, i) => (
            <div key={i} style={{ fontSize: 11, color: "#64748b", marginTop: 8, opacity: progress > i * 18 ? 1 : 0.25, transition: "opacity 0.4s", textAlign: "left" }}>
              <span style={{ color: progress > i * 18 ? "#10b981" : "#334155", marginRight: 8 }}>{progress > i * 18 ? "✓" : "○"}</span>{s}
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:0.7;transform:scale(1)} 50%{opacity:1;transform:scale(1.05)} }`}</style>
    </div>
  );

  // ── SCORING ───────────────────────────────────────────────────────────────
  if (step === "scoring" && fw) {
    const domain = fw.domains[activeDomain];
    const domainScore = scores[domain.id];

    return (
      <div style={css.root}>
        <div style={css.bg} />
        {/* Topbar */}
        <div style={css.topbar}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Logo small />
            <span style={{ fontSize: 12, color: "#f1f5f9", fontWeight: 700 }}>{orgName}</span>
            <span style={{ ...css.fwBadge, background: fw.color + "20", color: fw.color, border: `1px solid ${fw.color}30`, fontSize: 9 }}>{fw.name}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {error && <span style={{ fontSize: 11, color: "#ef4444" }}>{error}</span>}
            <div style={{ fontSize: 10, color: "#475569" }}>Domain {activeDomain + 1}/{fw.domains.length}</div>
            <button style={{ ...css.primaryBtn, background: fw.color, padding: "8px 16px", fontSize: 11 }} onClick={runAnalysis}>
              Generate Report →
            </button>
          </div>
        </div>

        <div style={css.scoringLayout}>
          {/* Domain nav */}
          <aside style={css.aside}>
            {fw.domains.map((d, i) => {
              const sc = scores[d.id];
              const isActive = i === activeDomain;
              let pct = 0;
              if (fw.scoring.type === "tier") pct = ((sc?.current ?? 1) / 4) * 100;
              else pct = ((sc?.controls?.filter(c => c === "MET").length ?? 0) / d.controls.length) * 100;

              return (
                <button key={d.id} style={{ ...css.domainBtn, ...(isActive ? { background: "rgba(255,255,255,0.07)", borderLeftColor: d.color } : {}) }}
                  onClick={() => setActiveDomain(i)}>
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: isActive ? "#f1f5f9" : "#94a3b8" }}>{d.name}</div>
                    {d.priority && <div style={{ fontSize: 9, color: "#475569", marginTop: 1 }}>{d.priority}</div>}
                  </div>
                  <div style={{ width: 32, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, flexShrink: 0 }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: d.color, borderRadius: 2, transition: "width 0.3s" }} />
                  </div>
                </button>
              );
            })}
          </aside>

          {/* Scoring panel */}
          <main style={css.scoringMain}>
            <div style={css.domainHeader}>
              <div style={{ ...css.domainId, color: domain.color, background: domain.color + "15", border: `1px solid ${domain.color}35` }}>{domain.id}</div>
              <div>
                <h2 style={{ ...css.domainTitle, color: domain.color }}>{domain.name}</h2>
                {domain.priority && (
                  <span style={{ fontSize: 10, color: fw.priorityDeadlines?.[domain.priority]?.includes("SANCTIONABLE") ? "#ef4444" : "#f59e0b", fontWeight: 700 }}>
                    {fw.priorityDeadlines?.[domain.priority] ?? domain.priority}
                  </span>
                )}
              </div>
            </div>

            {fw.scoring.type === "tier" ? (
              // NIST CSF tier sliders
              <div style={css.tierWrap}>
                <div style={css.tierRow}>
                  <div style={css.tierLabel}>Current Tier</div>
                  <div style={css.tierBtns}>
                    {[1,2,3,4].map(t => (
                      <button key={t}
                        style={{ ...css.tierBtn,
                          ...(domainScore?.current === t ? { background: domain.color, color: "#fff", borderColor: domain.color } : {}) }}
                        onClick={() => setTier(domain.id, "current", t)}>
                        T{t}
                        <span style={{ fontSize: 9, display: "block", marginTop: 1 }}>
                          {fw.scoring.labels[t]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={css.tierRow}>
                  <div style={css.tierLabel}>Target Tier</div>
                  <div style={css.tierBtns}>
                    {[1,2,3,4].map(t => (
                      <button key={t}
                        style={{ ...css.tierBtn,
                          ...(domainScore?.target === t ? { background: "#6366f1", color: "#fff", borderColor: "#6366f1" } : {}) }}
                        onClick={() => setTier(domain.id, "target", t)}>
                        T{t}
                        <span style={{ fontSize: 9, display: "block", marginTop: 1 }}>
                          {fw.scoring.labels[t]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={css.controlsGrid}>
                  {domain.controls.map((ctrl, i) => (
                    <div key={i} style={css.ctrlChip}>
                      <span style={{ ...css.ctrlDot, background: domain.color }} />
                      {ctrl}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // CMMC / CJIS MET/PARTIAL/NOT MET
              <div style={css.controlsList}>
                {domain.controls.map((ctrl, i) => {
                  const val = domainScore?.controls?.[i] ?? "NOT MET";
                  const opts = fw.scoring.options;
                  return (
                    <div key={i} style={css.ctrlRow}>
                      <div style={css.ctrlName}>{ctrl}</div>
                      <div style={css.ctrlToggle}>
                        {opts.map(opt => (
                          <button key={opt}
                            style={{ ...css.optBtn,
                              ...(val === opt ? {
                                background: (fw.scoring.colors[opt] || "#64748b") + "25",
                                color: fw.scoring.colors[opt] || "#64748b",
                                borderColor: (fw.scoring.colors[opt] || "#64748b") + "60",
                                fontWeight: 700,
                              } : {}) }}
                            onClick={() => setControlScore(domain.id, i, opt)}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Nav */}
            <div style={css.navBtns}>
              <button style={css.ghostBtn2} disabled={activeDomain === 0} onClick={() => setActiveDomain(p => p - 1)}>← Previous</button>
              {activeDomain < fw.domains.length - 1
                ? <button style={{ ...css.primaryBtn, background: domain.color }} onClick={() => setActiveDomain(p => p + 1)}>Next Domain →</button>
                : <button style={{ ...css.primaryBtn, background: fw.color }} onClick={runAnalysis}>Generate Report →</button>
              }
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ── RESULTS ───────────────────────────────────────────────────────────────
  if (step === "results" && results && fw) {
    const ratingColors = { "Critical Risk": "#ef4444", "High Risk": "#f97316", "Moderate Risk": "#f59e0b", "Managed Risk": "#10b981", "Compliant": "#6366f1" };
    const rc = ratingColors[results.overallRating] || "#64748b";
    const sevColor = { Critical: "#ef4444", High: "#f97316", Medium: "#f59e0b", Low: "#10b981" };

    return (
      <div style={css.root}>
        <div style={css.bg} />
        {/* Header */}
        <div style={css.topbar}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Logo small />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>{orgName}</div>
              <div style={{ fontSize: 10, color: "#475569" }}>{fw.name} Assessment · {new Date().toLocaleDateString()}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ ...css.ratingChip, color: rc, borderColor: rc + "50", background: rc + "15" }}>{results.overallRating}</div>
            {results.sprsScore !== undefined && (
              <div style={{ ...css.ratingChip, color: results.sprsScore >= 90 ? "#10b981" : results.sprsScore >= 70 ? "#f59e0b" : "#ef4444",
                borderColor: "#ffffff30", background: "rgba(255,255,255,0.05)" }}>
                SPRS: {results.sprsScore}/110
              </div>
            )}
            {results.p1GapsCount !== undefined && results.p1GapsCount > 0 && (
              <div style={{ ...css.ratingChip, color: "#ef4444", borderColor: "#ef444450", background: "#ef444415" }}>
                ⚠ {results.p1GapsCount} P1 Gaps
              </div>
            )}
            <button style={css.ghostBtn2} onClick={() => setStep("scoring")}>← Edit</button>
            <button style={css.ghostBtn2} onClick={() => { setStep("select"); setResults(null); }}>New Assessment</button>
          </div>
        </div>

        <div style={css.resultsBody}>
          {/* Row 1: Summary + Domain Bars */}
          <div style={css.row2col}>
            <div style={css.card}>
              <div style={css.sectionLbl}>EXECUTIVE SUMMARY</div>
              <p style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.8, margin: "0 0 20px" }}>{results.executiveSummary}</p>
              <div style={css.sectionLbl}>QUICK WINS</div>
              {results.quickWins?.map((qw, i) => (
                <div key={i} style={css.qwRow}>
                  <div style={css.qwNum}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", marginBottom: 3 }}>{qw.action}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Tag color="#10b981">{qw.impact} impact</Tag>
                      <Tag color="#64748b">{qw.effort} effort</Tag>
                      <Tag color={fw.color}>{qw.owner}</Tag>
                      <Tag color="#94a3b8">~{qw.days}d</Tag>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={css.card}>
              <div style={css.sectionLbl}>DOMAIN COMPLIANCE</div>
              <div style={{ marginTop: 8 }}>
                {results.domainScores?.map((d, i) => (
                  <DomainBar key={i} name={d.domain} score={d.score} color={d.color || fw.color} />
                ))}
              </div>
            </div>
          </div>

          {/* Row 2: Findings */}
          <div style={css.card}>
            <div style={css.sectionLbl}>TOP FINDINGS</div>
            <div style={css.findGrid}>
              {results.topFindings?.map((f, i) => (
                <div key={i} style={{ ...css.findCard, borderTopColor: sevColor[f.severity] || "#64748b" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                    <span style={{ ...css.sevBadge, color: sevColor[f.severity], background: (sevColor[f.severity] || "#64748b") + "18", border: `1px solid ${(sevColor[f.severity] || "#64748b")}30` }}>{f.severity}</span>
                    <span style={{ fontSize: 10, color: "#475569", fontWeight: 700 }}>{f.domainId}</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#e2e8f0", marginBottom: 6 }}>{f.domainName}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.6, marginBottom: 7 }}>{f.finding}</div>
                  <div style={css.riskBox}>⚠ {f.riskIfUnaddressed}</div>
                  <div style={css.recBox}>→ {f.recommendation}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Row 3: Roadmap */}
          <div style={css.card}>
            <div style={css.sectionLbl}>IMPROVEMENT ROADMAP</div>
            <div style={css.roadGrid}>
              {results.roadmap?.map((ph, pi) => {
                const phColor = ["#10b981","#f59e0b","#6366f1"][pi] || "#64748b";
                return (
                  <div key={pi}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: phColor, borderBottom: `1px solid ${phColor}35`, paddingBottom: 7, marginBottom: 10 }}>{ph.phase}</div>
                    {ph.initiatives?.map((init, ii) => (
                      <div key={ii} style={css.roadItem}>
                        <span style={{ color: phColor, marginRight: 6 }}>▸</span>{init}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ textAlign: "center", fontSize: 10, color: "#1e293b", padding: "12px 0 4px" }}>
            CyberEye Solutions · {fw.name} Assessment · Powered by Anthropic Claude
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function Logo({ small }) {
  return (
    <svg width={small ? 20 : 28} height={small ? 20 : 28} viewBox="0 0 28 28">
      <polygon points="14,2 26,8 26,20 14,26 2,20 2,8" fill="none" stroke="#0ea5e9" strokeWidth="1.5"/>
      <polygon points="14,7 22,11 22,17 14,21 6,17 6,11" fill="none" stroke="#0ea5e9" strokeWidth="0.8" opacity="0.4"/>
      <circle cx="14" cy="14" r="3" fill="#0ea5e9"/>
    </svg>
  );
}

function Tag({ color, children }) {
  return <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: color + "20", color, border: `1px solid ${color}35` }}>{children}</span>;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const css = {
  root: { minHeight: "100vh", background: "#060b14", color: "#e2e8f0", fontFamily: "'IBM Plex Mono','Courier New',monospace", position: "relative" },
  bg: { position: "fixed", inset: 0, background: "radial-gradient(ellipse 80% 50% at 20% -10%, rgba(14,165,233,0.07) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 80% 110%, rgba(99,102,241,0.06) 0%, transparent 60%)", pointerEvents: "none" },
  center: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24, position: "relative", zIndex: 1 },

  // Select
  selectHeader: { textAlign: "center", marginBottom: 32, maxWidth: 560 },
  selectTitle: { fontSize: 26, fontWeight: 800, color: "#f1f5f9", margin: "12px 0 8px" },
  selectSub: { fontSize: 13, color: "#64748b", margin: 0 },
  fwGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, maxWidth: 800, width: "100%" },
  fwCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderTop: "2px solid", borderRadius: 12, padding: "20px 18px", cursor: "pointer", textAlign: "center", transition: "all 0.15s", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 },
  fwBadge: { fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 4, letterSpacing: "0.08em" },
  fwIcon: { fontSize: 28, lineHeight: 1 },
  fwName: { fontSize: 16, fontWeight: 800, color: "#f1f5f9" },
  fwSub: { fontSize: 11, color: "#94a3b8" },
  fwAudience: { fontSize: 10, color: "#475569", padding: "2px 8px", background: "rgba(255,255,255,0.04)", borderRadius: 20 },
  fwTagline: { fontSize: 11, color: "#64748b", lineHeight: 1.5, marginTop: 4 },
  fwStart: { fontSize: 11, fontWeight: 700, marginTop: 8 },

  // Intake
  intakeCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "36px 40px", maxWidth: 520, width: "100%" },
  backBtn: { background: "none", border: "none", color: "#475569", fontSize: 11, cursor: "pointer", fontFamily: "inherit", marginBottom: 16, padding: 0 },
  intakeTitle: { fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: "0 0 8px" },
  intakeSub: { fontSize: 12, color: "#64748b", margin: "0 0 16px", lineHeight: 1.6 },
  cjisAlert: { background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 6, padding: "10px 12px", fontSize: 11, color: "#94a3b8", lineHeight: 1.6, marginBottom: 20 },
  intakeForm: { display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 },
  formField: { display: "flex", flexDirection: "column", gap: 5 },
  label: { fontSize: 10, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase" },
  input: { padding: "9px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#e2e8f0", fontSize: 12, fontFamily: "inherit", outline: "none" },
  primaryBtn: { padding: "10px 20px", border: "none", borderRadius: 7, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.03em" },

  // Progress
  progressTrack: { height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden", marginBottom: 6 },
  progressFill: { height: "100%", borderRadius: 2, transition: "width 0.2s" },

  // Scoring
  topbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(6,11,20,0.95)", backdropFilter: "blur(10px)", position: "sticky", top: 0, zIndex: 20, flexWrap: "wrap", gap: 8 },
  scoringLayout: { display: "flex", height: "calc(100vh - 52px)", position: "relative", zIndex: 1 },
  aside: { width: 195, flexShrink: 0, borderRight: "1px solid rgba(255,255,255,0.06)", padding: "12px 6px", overflowY: "auto", background: "rgba(6,11,20,0.6)" },
  domainBtn: { display: "flex", alignItems: "center", width: "100%", padding: "8px 10px", background: "none", border: "none", borderLeft: "2px solid transparent", borderRadius: "0 6px 6px 0", cursor: "pointer", marginBottom: 2, gap: 8, transition: "all 0.12s" },
  scoringMain: { flex: 1, overflowY: "auto", padding: "20px 24px" },
  domainHeader: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20, paddingBottom: 14, borderBottom: "1px solid rgba(255,255,255,0.06)" },
  domainId: { fontSize: 11, fontWeight: 800, padding: "3px 9px", borderRadius: 4, border: "1px solid", flexShrink: 0, letterSpacing: "0.06em" },
  domainTitle: { fontSize: 18, fontWeight: 700, margin: 0 },

  // Tier
  tierWrap: { display: "flex", flexDirection: "column", gap: 20 },
  tierRow: { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  tierLabel: { fontSize: 10, color: "#64748b", width: 80, flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.07em" },
  tierBtns: { display: "flex", gap: 8, flexWrap: "wrap" },
  tierBtn: { padding: "8px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#94a3b8", cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 600, textAlign: "center", minWidth: 72, transition: "all 0.12s" },
  controlsGrid: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 },
  ctrlChip: { display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#64748b", padding: "4px 10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 20 },
  ctrlDot: { width: 5, height: 5, borderRadius: "50%", flexShrink: 0 },

  // Controls list
  controlsList: { display: "flex", flexDirection: "column", gap: 6 },
  ctrlRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, flexWrap: "wrap" },
  ctrlName: { fontSize: 12, color: "#e2e8f0", flex: 1, minWidth: 160 },
  ctrlToggle: { display: "flex", gap: 4 },
  optBtn: { padding: "4px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, color: "#64748b", cursor: "pointer", fontSize: 10, fontFamily: "inherit", fontWeight: 600, transition: "all 0.1s" },

  navBtns: { display: "flex", justifyContent: "space-between", marginTop: 24, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)" },
  ghostBtn2: { padding: "8px 14px", background: "none", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#94a3b8", cursor: "pointer", fontSize: 11, fontFamily: "inherit" },

  // Results
  ratingChip: { fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 6, border: "1px solid", letterSpacing: "0.04em" },
  resultsBody: { overflowY: "auto", padding: "18px 22px", position: "relative", zIndex: 1 },
  row2col: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 },
  card: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "14px 18px", marginBottom: 14 },
  sectionLbl: { fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "#334155", marginBottom: 12, fontWeight: 700 },
  qwRow: { display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 },
  qwNum: { width: 22, height: 22, borderRadius: "50%", background: "rgba(14,165,233,0.15)", border: "1px solid rgba(14,165,233,0.3)", color: "#0ea5e9", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  findGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 12, marginTop: 8 },
  findCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "2px solid", borderRadius: 8, padding: "11px 13px" },
  sevBadge: { fontSize: 9, padding: "2px 7px", borderRadius: 10, fontWeight: 700, letterSpacing: "0.04em" },
  riskBox: { fontSize: 11, color: "#f59e0b", background: "rgba(245,158,11,0.07)", borderRadius: 4, padding: "4px 7px", marginBottom: 5, lineHeight: 1.5 },
  recBox: { fontSize: 11, color: "#10b981", background: "rgba(16,185,129,0.07)", borderRadius: 4, padding: "4px 7px", lineHeight: 1.5 },
  roadGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginTop: 8 },
  roadItem: { fontSize: 11, color: "#94a3b8", marginBottom: 7, lineHeight: 1.5 },
};
